import { hasPhrase, type StaffKey } from '../lib/staffText'
import type { Feed, FeedCheck } from '../lib/types'

/** What the Data feed screen says the feed is. */
export type FeedState = 'off' | 'waitingForDba' | 'on' | 'broken' | 'notOurs'

/** The versioned schema a BI tool reads, and the TLS mode the guide asks for. */
export const FEED_SCHEMA = 'report_v1'
export const FEED_SSLMODE = 'verify-full'

/**
 * A script waiting for the DBA comes first: after New password on a server that may
 * not change logins, the old password still connects but the next one waits. A
 * check with problems makes a feed that is on "broken"; while waiting, its problems
 * are the DBA's still to do.
 */
export function feedState(f: Feed, check?: FeedCheck): FeedState {
  // Never "Off" while a login of this name can sign in: it is not this server's to stop.
  if (f.loginNotOurs) return 'notOurs'
  if (f.awaitingDba) return 'waitingForDba'
  if (!f.enabled) return 'off'
  return check && check.problems.length > 0 ? 'broken' : 'on'
}

/**
 * The connection as the BI team is handed it: libpq keywords only, so it pastes into
 * any libpq connection string, never a password. The schema is not a libpq keyword;
 * the login's own search_path is report_v1, and the screen shows it apart.
 */
export function connectionText(f: Pick<Feed, 'host' | 'port' | 'database' | 'login'>): string {
  return [
    `host=${f.host}`,
    `port=${f.port}`,
    `dbname=${f.database}`,
    `user=${f.login}`,
    `sslmode=${FEED_SSLMODE}`,
  ].join('\n')
}

/** Where the file feed's first entity is fetched, on this server. */
export function feedFileAddress(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/api/v1/feed/visits`
}

/**
 * A check's problem in words, or undefined for a code this screen has none for.
 * Where the server makes the login itself, the repair is its own (New password),
 * not a script: "run the script" is the DBA path's words only.
 */
export function problemKey(code: string, serverMakesLogins = false): StaffKey | undefined {
  const keys = serverMakesLogins ? [`feed.problem.${code}.server`, `feed.problem.${code}`] : [`feed.problem.${code}`]
  return keys.find((key) => hasPhrase(key)) as StaffKey | undefined
}
